import { Page, View, Image, Text, Link } from '@react-pdf/renderer';
import { styles } from './styles';
import logoImage from '@assets/images/we_conect_logo_black.png';
import {
  Portfolio,
  PublicEventDetailsResponse,
  ResponsePaginationType,
} from '@hubs101/booking-api-skd-client/lib/types';
import { eventTimeFormat } from '@utils/time';
import { getImageSrc } from '@utils/fn';
import { useTranslation } from 'react-i18next';
import { APP_HOST } from '@utils/apiHooks';

export interface TitlePageProps {
  event: PublicEventDetailsResponse;
  coLocatedEvents: PublicEventDetailsResponse[] | undefined;
  portfolios: ResponsePaginationType<Portfolio> | undefined;
  footer: JSX.Element;
}

const TitlePage = ({
  event,
  coLocatedEvents,
  portfolios,
  footer,
}: TitlePageProps) => {
  const { t } = useTranslation();
  const citation = event.website_content?.citations?.[0] || {};
  const personDetails: string = citation
    ? [
        citation?.first_name || '',
        citation?.last_name || '',
        citation?.jobtitle || '',
        citation?.company || '',
      ]
        .filter(Boolean)
        .join(', ')
    : '';

  return (
    <Page size="A4" orientation="portrait" style={styles.page}>
      <View style={styles.topImageWrapper}>
        <Image
          src={
            'https://media.we-conect.com/images/pdf/agenda/pdf_agenda_title.png'
          }
          style={styles.topImage}
        />
      </View>

      <View style={styles.centerImageWrapper}>
        <Image
          src={getImageSrc(event?.images?.logo)}
          style={styles.centerImage}
        />

        <View style={styles.textContainer}>
          <Text style={styles.title}>{event?.name || ''}</Text>
          <Text style={styles.subtitle}>
            {eventTimeFormat(
              event?.start_date,
              event?.end_date,
              event?.language
            )}{' '}
            | {event?.location?.name}, {event?.location?.marker?.city}
          </Text>
        </View>
      </View>

      {personDetails ? (
        <View
          style={{
            ...styles.citationWrapper,
            backgroundColor: event?.design?.themeColor || 'black',
            color: event?.design?.headerFontColor || 'white',
          }}
        >
          <View style={styles.citationInnerWrapper}>
            <Text style={styles.citation}>“{citation.citation}”</Text>
            <View style={styles.line} />
            <Text style={styles.personDetails}>{personDetails}</Text>
          </View>
        </View>
      ) : (
        <View style={{ height: 120 }}></View>
      )}

      <View style={styles.threeColumnWrapper}>
        <View style={styles.column}>
          <Image
            src={
              logoImage
              /*
              getImageSrc(portfolios?.data?.[0]?.design?.logoPosImage || '')
              */
            }
            style={styles.leftImage}
          />
          <Text style={styles.leftText}>{event?.description}</Text>
        </View>

        <View style={styles.column}>
          <Text style={styles.middleText}>{t('secure-your-place-now')}</Text>
          <Link
            src={`${APP_HOST}/events/${event?.slug}${t('book-now-url')}`}
            style={{
              ...styles.linkButton,
              backgroundColor: event?.design?.themeColor || 'black',
            }}
          >
            {t('book-now')}
          </Link>

          <Link
            src={`${APP_HOST}/events/${event?.slug}${t('partnering-packages-url')}`}
            style={{
              ...styles.linkButton,
              backgroundColor: event?.design?.themeColor || 'black',
            }}
          >
            {t('apply-as-partner')}
          </Link>
        </View>

        {event?.colocated && (
          <View
            style={{
              ...styles.column,
              justifyContent: 'flex-start',
              alignItems: 'flex-end',
            }}
          >
            <Text style={styles.rightText}>
              {t('this-event-is-co-located-with')}
            </Text>
            {coLocatedEvents
              ?.filter(coLocatedEvent => coLocatedEvent.id !== event.id)
              ?.map((coLocatedEvent, index) => (
                <Image
                  key={index}
                  src={getImageSrc(coLocatedEvent.images.logo)}
                  style={styles.rightImage}
                />
              ))}
          </View>
        )}
      </View>

      {footer}
    </Page>
  );
};

export default TitlePage;
